import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getAdminToken, setAdminToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, Plus, Mail, MessageSquare, ArrowLeft, Trash2, Calendar, Play, TestTube, Layers } from "lucide-react";

const ALL_TAGS = [
  "registration_5k", "registration_1mile", "vendor", "sponsor", "volunteer", "donor",
  "team_captain", "wellness_partner", "fuel_partner",
  "warm_up", "gym_team", "cheer_zone", "raffle", "wellness_table",
  "manual", "csv_import",
];
const SOURCES = ["registrations", "vendors", "sponsors", "volunteers", "donations", "manual", "csv_import"];

const emptyCampaign = {
  name: "",
  channel: "email",
  subject: "",
  body: "",
  segment_tags: [],
  segment_sources: [],
  scheduled_at: "",
  steps: [],
};

const emptyStep = { delay_hours: 24, channel: "email", subject: "", body: "" };

export default function AdminCampaigns() {
  const nav = useNavigate();
  const [authed] = useState(!!getAdminToken());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list view; object = compose view
  const [form, setForm] = useState({ ...emptyCampaign });
  const [preview, setPreview] = useState({ total: 0, with_email: 0, with_phone: 0 });
  const [testTo, setTestTo] = useState({ email: "", phone: "" });
  const [dripMode, setDripMode] = useState(false);

  useEffect(() => {
    if (!authed) nav("/admin");
    else load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/campaigns");
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) { setAdminToken(null); nav("/admin"); }
      else toast.error("Could not load campaigns.");
    } finally { setLoading(false); }
  };

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) => updateForm("segment_tags", form.segment_tags.includes(t) ? form.segment_tags.filter((x) => x !== t) : [...form.segment_tags, t]);
  const toggleSource = (s) => updateForm("segment_sources", form.segment_sources.includes(s) ? form.segment_sources.filter((x) => x !== s) : [...form.segment_sources, s]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.post("/admin/campaigns/segment-preview", { segment_tags: form.segment_tags, segment_sources: form.segment_sources });
        setPreview(data);
      } catch { /* preview is best-effort */ }
    })();
  }, [form.segment_tags, form.segment_sources, editing]);

  const startNew = () => { setForm({ ...emptyCampaign }); setDripMode(false); setEditing({ id: null }); };
  const cancel = () => { setEditing(null); setForm({ ...emptyCampaign }); setDripMode(false); };

  const save = async () => {
    if (!form.name) { toast.error("Campaign name required."); return; }
    if (!dripMode && !form.body) { toast.error("Message body required."); return; }
    if (dripMode && form.steps.length === 0) { toast.error("Add at least one drip step."); return; }
    const payload = { ...form };
    // If drip mode, ignore top-level body/subject
    if (dripMode) { payload.body = ""; payload.subject = ""; }
    else { payload.steps = []; }
    if (!payload.scheduled_at) delete payload.scheduled_at;
    try {
      if (editing?.id) {
        await api.patch(`/admin/campaigns/${editing.id}`, payload);
        toast.success("Campaign updated.");
      } else {
        const { data } = await api.post("/admin/campaigns", payload);
        setEditing({ id: data.id });
        toast.success("Campaign saved.");
      }
      load();
    } catch { toast.error("Save failed."); }
  };

  const sendNow = async () => {
    if (!editing?.id) { toast.error("Save the campaign first."); return; }
    if (!window.confirm(`Send now to ${preview.total} contacts (${preview.with_email} email, ${preview.with_phone} phone)?`)) return;
    try {
      await api.post(`/admin/campaigns/${editing.id}/send`);
      toast.success("Campaign queued — sending in the background.");
      setTimeout(load, 2000);
    } catch { toast.error("Send failed."); }
  };

  const testSend = async () => {
    if (!editing?.id) { toast.error("Save the campaign first."); return; }
    if (!testTo.email && !testTo.phone) { toast.error("Enter a test email or phone."); return; }
    try {
      await api.post(`/admin/campaigns/${editing.id}/test-send`, testTo);
      toast.success("Test message sent.");
    } catch { toast.error("Test send failed."); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await api.delete(`/admin/campaigns/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const editExisting = (c) => {
    setForm({
      name: c.name || "", channel: c.channel || "email", subject: c.subject || "", body: c.body || "",
      segment_tags: c.segment_tags || [], segment_sources: c.segment_sources || [],
      scheduled_at: c.scheduled_at ? c.scheduled_at.slice(0, 16) : "",
      steps: c.steps || [],
    });
    setDripMode((c.steps || []).length > 0);
    setEditing({ id: c.id });
  };

  const addStep = () => updateForm("steps", [...form.steps, { ...emptyStep }]);
  const updateStep = (idx, patch) => updateForm("steps", form.steps.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const removeStep = (idx) => updateForm("steps", form.steps.filter((_, i) => i !== idx));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#27272A] bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#71717A] hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
            <div className="ml-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">Campaigns</div>
              <div className="font-display text-lg uppercase text-white flex items-center gap-2"><Send className="w-5 h-5" /> Email + SMS Outreach</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/contacts" data-testid="nav-contacts" className="border border-white/20 hover:border-[#EC008C] hover:text-[#EC008C] text-white font-bold uppercase tracking-widest text-xs px-4 py-2">Contacts →</Link>
            {!editing && (
              <>
                <button onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/campaigns/install-previous-vendor");
                    toast.success(data.created ? `Previous-vendor drip installed (${data.steps} emails).` : "Previous-vendor campaign already installed.");
                    load();
                  } catch { toast.error("Could not install."); }
                }} data-testid="install-prev-vendor-campaign" className="inline-flex items-center gap-2 border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-xs px-4 py-2">
                  <Plus className="w-3.5 h-3.5" /> Install Prev Vendor Drip
                </button>
                <button onClick={startNew} data-testid="new-campaign-btn" className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs px-4 py-2"><Plus className="w-3.5 h-3.5" /> New Campaign</button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {editing ? (
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="bg-[#141414] border border-[#27272A] p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl uppercase text-white">{editing.id ? "Edit Campaign" : "New Campaign"}</h2>
                <button onClick={cancel} className="text-xs uppercase tracking-widest text-[#71717A] hover:text-white">← Back to list</button>
              </div>

              <Field label="Campaign name (internal)">
                <Input data-testid="campaign-name" placeholder="e.g. Day 1 — Volunteer Call" value={form.name} onChange={(e) => updateForm("name", e.target.value)} className="bg-black border-[#27272A]" />
              </Field>

              <div className="flex gap-3">
                <ChannelBtn active={form.channel === "email"} onClick={() => updateForm("channel", "email")} icon={Mail} label="Email" testId="channel-email" />
                <ChannelBtn active={form.channel === "sms"} onClick={() => updateForm("channel", "sms")} icon={MessageSquare} label="SMS" testId="channel-sms" />
                <ChannelBtn active={form.channel === "both"} onClick={() => updateForm("channel", "both")} icon={Send} label="Both" testId="channel-both" />
              </div>

              <div className="flex items-center gap-3 border-t border-[#27272A] pt-4">
                <button onClick={() => setDripMode(false)} className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${!dripMode ? "bg-[#00A651] text-black border-[#00A651]" : "border-[#27272A] text-[#A3A3A3]"}`}>Single Broadcast</button>
                <button onClick={() => setDripMode(true)} data-testid="drip-mode-btn" className={`text-xs uppercase tracking-widest px-3 py-1.5 border flex items-center gap-1.5 ${dripMode ? "bg-[#EC008C] text-white border-[#EC008C]" : "border-[#27272A] text-[#A3A3A3]"}`}><Layers className="w-3 h-3" /> Drip Sequence</button>
              </div>

              {!dripMode ? (
                <>
                  {form.channel !== "sms" && (
                    <Field label="Email subject">
                      <Input data-testid="campaign-subject" value={form.subject} onChange={(e) => updateForm("subject", e.target.value)} className="bg-black border-[#27272A]" placeholder="Race day doesn't run without YOU."/>
                    </Field>
                  )}
                  <Field label="Message body (personalize with {first_name}, {last_name}, {company})">
                    <Textarea data-testid="campaign-body" rows={10} value={form.body} onChange={(e) => updateForm("body", e.target.value)} className="bg-black border-[#27272A] font-mono text-sm" placeholder="Hi {first_name},&#10;&#10;Race day doesn't run without YOU..." />
                  </Field>
                  <Field label="Schedule for later (optional — UTC)">
                    <Input data-testid="campaign-schedule" type="datetime-local" value={form.scheduled_at} onChange={(e) => updateForm("scheduled_at", e.target.value)} className="bg-black border-[#27272A]" />
                  </Field>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="text-[11px] uppercase tracking-widest text-[#A3A3A3]">Drip sequence — each step fires <em>after the previous step&apos;s delay</em>, starting when campaign is triggered.</div>
                  {form.steps.map((s, i) => (
                    <div key={i} className="bg-black border border-[#27272A] p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Step {i + 1}</div>
                        <button onClick={() => removeStep(i)} className="text-[#EC008C] hover:text-white"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Field label="Delay after prior step (hours)">
                          <Input type="number" step="0.5" value={s.delay_hours} onChange={(e) => updateStep(i, { delay_hours: parseFloat(e.target.value) })} className="bg-[#0A0A0A] border-[#27272A]" />
                        </Field>
                        <Field label="Channel">
                          <select value={s.channel} onChange={(e) => updateStep(i, { channel: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm rounded-md">
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                            <option value="both">Both</option>
                          </select>
                        </Field>
                      </div>
                      {s.channel !== "sms" && (
                        <Field label="Subject (email)"><Input value={s.subject} onChange={(e) => updateStep(i, { subject: e.target.value })} className="bg-[#0A0A0A] border-[#27272A]" /></Field>
                      )}
                      <Field label="Body">
                        <Textarea rows={5} value={s.body} onChange={(e) => updateStep(i, { body: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] font-mono text-sm" />
                      </Field>
                    </div>
                  ))}
                  <button onClick={addStep} data-testid="add-drip-step" className="w-full border-2 border-dashed border-[#27272A] hover:border-[#00A651] hover:text-[#00A651] text-[#71717A] font-bold uppercase tracking-widest text-xs py-3">+ Add Step</button>
                  <Field label="Schedule kickoff (optional — UTC)">
                    <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => updateForm("scheduled_at", e.target.value)} className="bg-black border-[#27272A]" />
                  </Field>
                </div>
              )}

              <div className="border-t border-[#27272A] pt-5 space-y-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Segment — who receives this?</div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Match any of these tags</Label>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {ALL_TAGS.map((t) => (
                      <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`seg-tag-${t}`} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border ${form.segment_tags.includes(t) ? "bg-[#EC008C] text-white border-[#EC008C]" : "border-[#27272A] text-[#A3A3A3] hover:border-white/40"}`}>{t}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">…or any of these sources</Label>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {SOURCES.map((s) => (
                      <button key={s} type="button" onClick={() => toggleSource(s)} data-testid={`seg-source-${s}`} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border ${form.segment_sources.includes(s) ? "bg-[#00A651] text-black border-[#00A651]" : "border-[#27272A] text-[#A3A3A3] hover:border-white/40"}`}>{s}</button>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#71717A]">Leave both empty to send to <span className="text-white">every contact</span>.</p>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="bg-[#141414] border border-[#00A651]/40 p-5 h-max sticky top-24 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Audience Preview</div>
                <div className="mt-2 font-display text-4xl font-black text-white leading-none" data-testid="segment-count">{preview.total.toLocaleString()}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-[#A3A3A3]">contacts</div>
                <div className="mt-3 text-xs text-[#E5E5E5]">
                  <div className="flex justify-between"><span>Email reachable</span><span className="text-[#00A651] font-bold">{preview.with_email}</span></div>
                  <div className="flex justify-between mt-1"><span>SMS reachable</span><span className="text-[#EC008C] font-bold">{preview.with_phone}</span></div>
                </div>
              </div>

              <button onClick={save} data-testid="campaign-save-btn" className="w-full bg-white text-black hover:bg-[#E5E5E5] font-bold uppercase tracking-widest text-xs py-2.5">Save Draft</button>

              {editing.id && (
                <>
                  <div className="border-t border-[#27272A] pt-4 space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Send a test</div>
                    <Input data-testid="test-email" placeholder="Test email" value={testTo.email} onChange={(e) => setTestTo({ ...testTo, email: e.target.value })} className="bg-black border-[#27272A] text-xs" />
                    <Input data-testid="test-phone" placeholder="+1 test phone" value={testTo.phone} onChange={(e) => setTestTo({ ...testTo, phone: e.target.value })} className="bg-black border-[#27272A] text-xs" />
                    <button onClick={testSend} data-testid="test-send-btn" className="w-full border border-white/30 hover:border-[#EC008C] hover:text-[#EC008C] text-white font-bold uppercase tracking-widest text-xs py-2 flex items-center justify-center gap-1.5"><TestTube className="w-3.5 h-3.5" /> Send Test</button>
                  </div>

                  <div className="border-t border-[#27272A] pt-4 space-y-2">
                    {form.scheduled_at ? (
                      <>
                        <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1"><Calendar className="w-3 h-3" /> Scheduled</div>
                        <div className="text-xs text-white">{form.scheduled_at}</div>
                      </>
                    ) : (
                      <button onClick={sendNow} data-testid="send-now-btn" className="w-full bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs py-3 flex items-center justify-center gap-1.5"><Play className="w-3.5 h-3.5" /> Send Now</button>
                    )}
                  </div>
                </>
              )}
            </aside>
          </div>
        ) : (
          <div className="bg-[#141414] border border-[#27272A]">
            {loading ? (
              <div className="p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">No campaigns yet. Click <span className="text-white">New Campaign</span> to build your first.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-black text-[#A3A3A3] uppercase tracking-widest text-[10px]">
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Channel</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Sent / Failed</th>
                    <th className="text-left px-4 py-3">Scheduled</th>
                    <th className="text-left px-4 py-3">Created</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} data-testid={`campaign-row-${c.id}`} className="border-t border-[#27272A] hover:bg-[#1A1A1A] cursor-pointer" onClick={() => editExisting(c)}>
                      <td className="px-4 py-3 font-bold">{c.name}</td>
                      <td className="px-4 py-3 uppercase text-[10px] tracking-widest text-[#EC008C]">{(c.steps || []).length > 0 ? `drip · ${c.steps.length}` : c.channel}</td>
                      <td className="px-4 py-3 uppercase text-[10px] tracking-widest"><StatusPill status={c.status} /></td>
                      <td className="px-4 py-3 text-[#E5E5E5]">{c.sent_count || 0} / <span className="text-[#EC008C]">{c.failed_count || 0}</span></td>
                      <td className="px-4 py-3 text-[#A3A3A3] text-xs">{(c.scheduled_at || "—").replace("T", " ").slice(0, 16)}</td>
                      <td className="px-4 py-3 text-[#A3A3A3] text-xs">{(c.created_at || "").replace("T", " ").slice(0, 16)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); del(c.id); }} data-testid={`campaign-delete-${c.id}`} className="text-[#EC008C] hover:text-white p-1"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.24em] text-[#A3A3A3]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ChannelBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`flex items-center gap-2 px-4 py-2.5 border-2 font-bold uppercase tracking-widest text-xs transition-colors ${active ? "bg-[#00A651] text-black border-[#00A651]" : "border-[#27272A] text-[#A3A3A3] hover:border-white/40"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function StatusPill({ status }) {
  const styles = {
    draft: "bg-[#27272A] text-[#A3A3A3]",
    scheduled: "bg-[#EC008C]/20 text-[#EC008C]",
    sending: "bg-yellow-500/20 text-yellow-400",
    sent: "bg-[#00A651]/20 text-[#00A651]",
    failed: "bg-red-500/20 text-red-400",
  };
  return <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${styles[status] || styles.draft}`}>{status || "draft"}</span>;
}
